import { initializeClientTelemetry, trackEvent } from './app-insights';
import { decodeTokenAndTrackException } from './get-decoded-button-token';
import {
  AccessButtonEnvironment,
  AccessButtonOptions,
  ApplicationInsightsLoggingData,
  Environment,
  LoadComponentsOptions,
  ProductTitle,
} from './types';

type ProductLocation = {
  uri: string;
  title?: ProductTitle;
};

declare global {
  interface Window {
    initialiseAccessButton: (options: AccessButtonOptions) => Promise<any>;
    AccessEvo: {
      initialiseAccessButton?: (options: AccessButtonOptions) => Promise<any>;
      setProductLocation?: (options: ProductLocation) => Promise<void>;
    };
  }
}

const SHELL_HEADER_NAME = 'access-shell';
const SHELL_BUTTON_SLOT_NAME = 'top-bar-access-button';
const SHELL_BUTTON_CLASS_NAME = `shellButtonSlot`;
const BUTTON_HEADER_NAME = 'access-button';
const COMPONENTS_SCRIPT_ID = 'access-button-components-script';
const NUMBER_OF_RETRIES = 1;
const FETCH_TIMEOUT = 3000;

function handleFetch(
  url: string,
  handleError: () => void,
  retries = NUMBER_OF_RETRIES,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    const { signal } = controller;
    const timeoutId = setTimeout(() => controller.abort('Fetch request timed out'), FETCH_TIMEOUT);
    const options = retries !== 0 ? { signal } : {};

    fetch(url, options)
      .then((response) => {
        clearTimeout(timeoutId);
        if (response.ok) {
          resolve(response);
          return;
        } else if (retries === 0) {
          reject(new Error(response.statusText));
          return;
        }
        handleError();
        handleFetch(url, handleError, --retries).then(resolve, reject);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        if (retries === 0) {
          reject(error);
          return;
        }
        handleError();
        handleFetch(url, handleError, --retries).then(resolve, reject);
      });
  });
}

function componentsLoaded(): boolean {
  const script = document.head.querySelector<HTMLScriptElement>(`#${COMPONENTS_SCRIPT_ID}`);
  return !!script;
}

let loadComponentsPromise: Promise<unknown> | null = null;
function getLoadComponentsPromise(
  getLoadComponentsCallback: () => Promise<unknown>,
): Promise<unknown> {
  let promise: null | Promise<unknown> = loadComponentsPromise;
  if (!promise) {
    promise = getLoadComponentsCallback().finally(() => {
      loadComponentsPromise = null;
      promise = null;
    });
    loadComponentsPromise = promise;
  }
  return promise;
}

let getComponentVersionAttempts = 0;
export async function loadComponents({ environment }: LoadComponentsOptions) {
  getComponentVersionAttempts = 0;
  async function _loadComponents() {
    return new Promise((resolve, reject) => {
      let componentsAlreadyLoaded = componentsLoaded();
      if (componentsAlreadyLoaded) {
        resolve({ status: true });
        return;
      }

      const url = `${getSrcValue(environment)}/api/component-version?version=${
        import.meta.env.VITE_ACCESS_BUTTON_LAUNCHER_VERSION
      }`;
      let BUTTON_HEADER_URL: string;
      const trackRetryCount = () => (getComponentVersionAttempts += 1);

      handleFetch(url, trackRetryCount)
        .then(async (response) => {
          let json = await response.json();
          BUTTON_HEADER_URL = json.bundleUrl;

          if (customElements.get(BUTTON_HEADER_NAME)) {
            resolve({ status: true });
          } else {
            try {
              const scriptEle = document.createElement('script');
              scriptEle.type = 'module';
              scriptEle.async = false;
              scriptEle.src = BUTTON_HEADER_URL;
              scriptEle.id = COMPONENTS_SCRIPT_ID;

              scriptEle.addEventListener('load', () => {
                resolve({ status: true });
              });

              scriptEle.addEventListener('error', () => {
                reject({
                  status: false,
                  message: `Failed to load the script ${BUTTON_HEADER_URL}`,
                });
              });

              document.head.appendChild(scriptEle);
            } catch (error) {
              reject(error);
            }
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
  return await getLoadComponentsPromise(() => _loadComponents());
}

function getParentElement(existingShellHeader: any, options: AccessButtonOptions) {
  if (existingShellHeader) {
    return document.querySelector(`.${SHELL_BUTTON_CLASS_NAME}`);
  }
  if (options.positions?.parentElement) {
    return document.querySelector(options.positions.parentElement);
  }
  return document.body;
}

function addHeader(options: AccessButtonOptions) {
  const existingShellHeader: any = document.querySelector(SHELL_HEADER_NAME);
  const { environment, ...restOfOptions } = options;
  const mappedOptions = {
    ...restOfOptions,
    src: getSrcValue(options.environment),
    appInsightsConnectionString: getAppInsightsEnvironment(options.environment),
    // in shell we can provide a default positions prop if not provided
    ...(existingShellHeader && !restOfOptions.positions
      ? { positions: { parentElement: `.${SHELL_BUTTON_CLASS_NAME}` } }
      : {}),
    // otherwise if fixed positions are provided
    // we need to ignore the desktop value
    // and set the default parentElement
    ...(existingShellHeader && restOfOptions.positions
      ? {
          positions: (() => {
            const { desktop, parentElement, ...rest } = restOfOptions.positions;
            return { parentElement: `.${SHELL_BUTTON_CLASS_NAME}`, ...rest };
          })(),
        }
      : {}),
  };
  if (existingShellHeader) {
    existingShellHeader.options = {
      ...mappedOptions,
      environment: options.environment,
    };
    window.dispatchEvent(new CustomEvent('shell-options'));

    // inject the slot for the button inside the shell component
    // this way we can control the full placement of the button now and in the future
    const div = document.createElement('div');
    div.setAttribute('slot', SHELL_BUTTON_SLOT_NAME);
    div.className = SHELL_BUTTON_CLASS_NAME;
    existingShellHeader.appendChild(div);
  }
  const existingHeader: any = document.querySelector(BUTTON_HEADER_NAME);
  if (existingHeader) {
    existingHeader.options = mappedOptions;
    return existingHeader;
  } else {
    const newHeader: any = document.createElement(BUTTON_HEADER_NAME);
    newHeader.options = mappedOptions;

    const parent = getParentElement(existingShellHeader, options);
    if (!parent) {
      throw new Error('Unable to find parentElement');
    }
    parent.appendChild(newHeader);
    return newHeader;
  }
}

export async function initialiseAccessButton(options: AccessButtonOptions) {
  initializeClientTelemetry(getAppInsightsEnvironment(options.environment));
  const loggingData: ApplicationInsightsLoggingData = {
    ...options,
    headerIsExtracted: options.positions !== undefined,
    launcherVersion: import.meta.env.VITE_ACCESS_BUTTON_LAUNCHER_VERSION,
    retries: 0,
  };

  return new Promise((resolve, reject) => {
    if (!document.body) {
      const error = new Error('Unable to initialise Access Button. No document body');
      console.warn(error);
      decodeTokenAndTrackException(
        { ...loggingData, retries: getComponentVersionAttempts },
        error,
        4,
      );
    }

    loadComponents({ environment: options.environment })
      .then(() => {
        const wc = addHeader(options);
        wc.addEventListener('wsApiReady', (e: CustomEvent) => {
          console.info('Access Button initialised with the following options', options);
          trackEvent('Access Button Initialised', loggingData);
          resolve(wc);
        });
      })
      .catch(async (err) => {
        console.error('Failed to initialise Access Button', err);
        decodeTokenAndTrackException(
          { ...loggingData, retries: getComponentVersionAttempts },
          err,
          4,
        );
      });
  });
}

async function waitForShell(): Promise<any> {
  return await Promise.race([
    new Promise<any>((resolve) => {
      customElements.whenDefined(SHELL_HEADER_NAME).then(() => {
        const interval = setInterval(() => {
          const shell = document.querySelector(SHELL_HEADER_NAME);
          if (shell) {
            clearInterval(interval);
            resolve(shell);
          }
        }, 50); // Check every 50ms
      });
    }),
    new Promise<void>(
      (_, reject) => setTimeout(() => reject(new Error('Timeout waiting for shell')), 10000), // 10-second timeout
    ),
  ]);
}

export async function setProductLocation(location: ProductLocation) {
  // shell only api
  // this will store the location of the open product

  // we should only fire this when the element is defined
  // and we can find it in the DOM
  const shell = await waitForShell();
  if (shell) {
    await shell.setLocation(location);
  }
}

if (typeof window !== 'undefined') {
  // for backwards compatibilty
  // can be removed in next major version
  window.initialiseAccessButton = initialiseAccessButton;

  // api for components exposed on a window object
  window.AccessEvo = {};
  window.AccessEvo.initialiseAccessButton = initialiseAccessButton;
  window.AccessEvo.setProductLocation = setProductLocation;
}

const getSrcValue = (environment: Environment) => {
  switch (environment) {
    case 'prod':
      return AccessButtonEnvironment.PRODUCTION;
    case 'prod-anz':
      return AccessButtonEnvironment.PROD_ANZ;
    case 'prod-us':
      return AccessButtonEnvironment.PROD_US;
    case 'prod-eu':
      return AccessButtonEnvironment.PROD_EU;
    case 'demo':
      return AccessButtonEnvironment.DEMO;
    case 'staging':
      return AccessButtonEnvironment.STAGING;
    case 'loadtest' as Environment:
      return AccessButtonEnvironment.LOADTEST;
    case 'preprod':
      return AccessButtonEnvironment.PREPROD;
    case 'integration-us':
      return AccessButtonEnvironment.INTEGRATION_US;
    case 'integration-eu':
      return AccessButtonEnvironment.INTEGRATION_EU;
    default:
      console.info('Unknown environment provided', environment);
      return environment;
  }
};

const getAppInsightsEnvironment = (environment: Environment) => {
  switch (environment) {
    case 'prod':
      return import.meta.env.VITE_PROD_UK_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'prod-anz':
      return import.meta.env.VITE_PROD_APAC_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'prod-us':
      return import.meta.env.VITE_PROD_US_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'prod-eu':
      return import.meta.env.VITE_PROD_EU_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'demo':
      return import.meta.env.VITE_DEMO_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'staging':
      return import.meta.env.VITE_STAGING_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'loadtest' as Environment:
      return import.meta.env.VITE_LOADTEST_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'preprod':
      return import.meta.env.VITE_PREPROD_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'integration-us':
      return import.meta.env.VITE_INTEGRATION_US_APPLICATIONINSIGHTS_CONNECTION_STRING;
    case 'integration-eu':
      return import.meta.env.VITE_INTEGRATION_EU_APPLICATIONINSIGHTS_CONNECTION_STRING;
    default:
      return;
  }
};
